/**
 * Handles updating the product title when hovering over color swatches on product pages.
 * When a user hovers over a color swatch, the last word of the product title (which is always the color) is replaced with the hovered color name.
 */

// Only initialize on product pages
// Check if we're on a product page by looking for product-specific elements
const isProductPage = document.querySelector('main[data-template="product"]') || 
                      (typeof Theme !== 'undefined' && Theme.template?.name === 'product') ||
                      document.querySelector('#ProductInformation');

if (isProductPage) {
  let originalTitle = null;
  let titleElement = null;

  /**
   * Find the product title element and store the original title
   */
  const findProductTitle = () => {
    const productTitleLink = document.querySelector('a[ref="productTitleLink"]');
    if (!productTitleLink) {
      return null;
    }

    const textElement = productTitleLink.querySelector('div, rte-formatter, p');
    titleElement = textElement || productTitleLink;

    if (titleElement) {
      const text = titleElement.textContent || titleElement.innerText || '';
      originalTitle = text.trim();
    }

    return titleElement;
  };

  /**
   * Get the base title without the last word (which is always the color)
   */
  const getBaseTitle = () => {
    if (!originalTitle) {
      return '';
    }
    
    // Split the title into words and remove the last word (the color)
    const words = originalTitle.trim().split(/\s+/);
    if (words.length <= 1) {
      // If there's only one word, return empty string (edge case)
      return '';
    }
    
    // Remove the last word and join the rest
    words.pop();
    return words.join(' ');
  };

  /**
   * Handle swatch hover enter
   */
  const handleSwatchEnter = (event) => {
    if (!titleElement) {
      findProductTitle();
    }

    if (!titleElement) {
      return;
    }

    const label = event.currentTarget || event.target.closest('label[data-color-name]');
    if (!label || !label.dataset.colorName) {
      return;
    }

    // Remove "- pre-order" from color name if present
    const colorName = label.dataset.colorName.replace(/\s*-\s*pre-order/gi, '').trim();

    // Store original title if not already stored
    if (!originalTitle) {
      const text = titleElement.textContent || titleElement.innerText || '';
      originalTitle = text.trim();
    }

    const baseTitle = getBaseTitle();
    // Replace the last word with the new color name
    const newTitle = baseTitle ? `${baseTitle} ${colorName}` : colorName;

    // Update the title text
    if (titleElement.tagName === 'RTEFORMATTER') {
      if (titleElement.innerHTML.includes('<')) {
        titleElement.innerHTML = newTitle;
      } else {
        titleElement.textContent = newTitle;
      }
    } else {
      titleElement.textContent = newTitle;
    }
  };

  /**
   * Handle swatch hover leave
   */
  const handleSwatchLeave = () => {
    if (!titleElement || !originalTitle) {
      return;
    }

    // Restore the original title
    if (titleElement.tagName === 'RTEFORMATTER') {
      if (titleElement.innerHTML.includes('<')) {
        titleElement.innerHTML = originalTitle;
      } else {
        titleElement.textContent = originalTitle;
      }
    } else {
      titleElement.textContent = originalTitle;
    }
  };

  /**
   * Initialize the swatch hover functionality
   */
  const init = () => {
    // Find and store the product title
    findProductTitle();

    // Track which labels already have listeners to avoid duplicates
    const labeledLabels = new WeakSet();

    // Find all swatch labels and attach listeners directly
    const attachListeners = () => {
      const swatchLabels = document.querySelectorAll(
        'label[data-color-name].variant-option__button-label--has-swatch'
      );

      for (const label of swatchLabels) {
        // Skip if already has listeners
        if (labeledLabels.has(label)) {
          continue;
        }

        // Mark as having listeners
        labeledLabels.add(label);

        // Attach listeners
        label.addEventListener('pointerenter', handleSwatchEnter);
        label.addEventListener('pointerleave', handleSwatchLeave);
        label.addEventListener('mouseenter', handleSwatchEnter);
        label.addEventListener('mouseleave', handleSwatchLeave);
      }
    };

    // Initial attachment
    attachListeners();

    // Watch for dynamically added swatches
    const observer = new MutationObserver(() => {
      attachListeners();
    });

    const swatchesComponent = document.querySelector('swatches-variant-picker-component');
    if (swatchesComponent) {
      observer.observe(swatchesComponent, {
        childList: true,
        subtree: true,
      });
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Watch for title element changes (e.g., via AJAX morphing)
  const titleObserver = new MutationObserver(() => {
    // Re-find the title element in case it was replaced
    const newTitleElement = findProductTitle();
    if (newTitleElement && newTitleElement !== titleElement) {
      // Title was replaced, reset the original title
      if (titleElement) {
        const text = titleElement.textContent || titleElement.innerText || '';
        if (text.trim() !== originalTitle) {
          // Title was modified, restore it before switching
          titleElement.textContent = originalTitle;
        }
      }
      // Reset original title so it gets captured from the new element
      originalTitle = null;
      findProductTitle();
    }
  });

  // Observe the product information section for changes
  const productInfoSection = document.querySelector('#ProductInformation');
  if (productInfoSection) {
    titleObserver.observe(productInfoSection, {
      childList: true,
      subtree: true,
    });
  }
}
